import { useLocation } from 'react-router'
import { useMutation } from '@tanstack/react-query'

import useQuery from '@shared/hooks/src/useQuery'
import useUserSetting from '@shared/hooks/src/useUserSetting'
import { useMe } from '@shared/providers/src/MeProvider'
import { setQueriesData, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { mapCache, QK, UserSettingName } from '@shared/utils'

export function useCBO(cboId, options = {}, config) {
  return useQuery({
    queryKey: QK.admin.cbos.id(cboId).details,
    queryFn: () => API.admin.cbos.id(cboId).details(undefined, config),
    ...options,
  })
}

export function useCBOStatusUpdate() {
  return useMutation({
    mutationFn: ({ id, status }) => API.admin.cbos.id(id).updateStatus({ status }),
    onSuccess: (response, { id, status }) => {
      setQueryData(QK.admin.cbos.id(id).details, (old) => (old ? { ...old, data: status } : old))

      return setQueriesData(
        { queryKey: QK.admin.cbos.lists },
        mapCache((old) => (old.id === id ? { ...old, data: status } : old))
      )
    },
  })
}

export function useCBOUpdate(cboId) {
  return useMutation({
    mutationFn: (data) => API.admin.cbos.id(cboId).update(data),
    onSuccess: (data) => {
      setQueryData(QK.admin.cbos.id(cboId).details, data)

      return setQueriesData(
        { queryKey: QK.admin.cbos.lists },
        mapCache((old) => (old.id === cboId ? { ...old, ...data } : old))
      )
    },
  })
}

export function useCBOContactUsers(cboId, options = {}) {
  return useQuery({
    queryKey: QK.admin.cbos.id(cboId).contactUsers.list(),
    queryFn: () => API.admin.cbos.id(cboId).contactUsers.list(),
    ...options,
  })
}

export function useCBOPossibleDefaultProviders(cboId, options = {}) {
  return useQuery({
    queryKey: QK.cbos.id(cboId).providers.possibleDefault.list(),
    queryFn: () => API.cbos.id(cboId).providers.possibleDefault(),
    staleTime: 0,
    ...options,
  })
}

export function useCBOContractedPharmacies(cboId, query, options = {}) {
  return useQuery({
    queryKey: QK.cbos.id(cboId).contractedPharmacies.list(query),
    queryFn: () => API.cbos.id(cboId).pharmacies.contracted.list(query),
    ...options,
  })
}

export function useSegment() {
  const location = useLocation()
  const segments = location.pathname.split('/').filter(Boolean)
  return segments[3] ?? ''
}

export function useCanManagePrescriptionSettings() {
  const me = useMe()
  const accessRights = useUserSetting(me, UserSettingName.AccessRights)
  return me.superAdmin || accessRights?.value?.prescription_file_setting === true
}
